import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Search, Star, ChevronDown, Loader2, AlertCircle, Target, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useReadingGoals } from '@/hooks/useReadingGoals';
import { StarRating } from '@/components/StarRating';
import { CoverImage } from '@/components/CoverImage';
import { OnboardingErrorCard } from '@/components/OnboardingErrorCard';
import { ReadingGoalSettings } from '@/components/ReadingGoalSettings';
import { PairwiseSheet } from '@/components/PairwiseSheet';
import { NotificationPreferences } from '@/components/NotificationPreferences';
import { FollowedTagsList } from '@/components/FollowedTagsList';
import { supabase } from '@/integrations/supabase/client';
import { 
  getUserGenres,
  saveUserGenres,
  saveBookRatings,
  POPULAR_BOOKS
} from '@/lib/onboarding';
import { GENRE_OPTIONS, FICTION_GENRES, NONFICTION_GENRES } from '@/lib/constants';

const AVAILABLE_GENRES = GENRE_OPTIONS;
const FICTION = FICTION_GENRES;
const NONFICTION = NONFICTION_GENRES;

interface BookData {
  id: string;
  title: string;
  authors: string[];
  cover_url?: string;
  isbn13?: string;
  open_library_id?: string;
  subjects?: string[];
}

interface BookRating {
  bookId: string;
  rating: number;
}

export default function Preferences() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const { getGoalForYear, createOrUpdateGoal, loading: goalLoading } = useReadingGoals();

  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [bookRatings, setBookRatings] = useState<Record<string, number>>({});
  const [booksByGenre, setBooksByGenre] = useState<Record<string, BookData[]>>({});
  const [loadMoreByGenre, setLoadMoreByGenre] = useState<Record<string, number>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'title' | 'author'>('title');
  const [searchResults, setSearchResults] = useState<BookData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Reading goals state
  const currentYear = new Date().getFullYear();
  const [targetBooks, setTargetBooks] = useState<string>('12');
  const [targetPages, setTargetPages] = useState<string>('');
  const [currentGoal, setCurrentGoal] = useState<any>(null);
  
  // Pairwise ranking state
  const [showPairwise, setShowPairwise] = useState(false);
  const [pairwiseData, setPairwiseData] = useState<any>(null);
  const [currentRatingBook, setCurrentRatingBook] = useState<BookData | null>(null);

  // Debug mode for dev
  const debugMode = import.meta.env.DEV && new URLSearchParams(location.search).has('debug');

  // Load user's existing preferences
  useEffect(() => {
    if (user) {
      loadUserPreferences();
    }
  }, [user]);

  const loadUserPreferences = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      // Load user genres
      const genres = await getUserGenres(user.id);
      setSelectedGenres(genres);

      // Initialize load more counters
      const loadMoreInit: Record<string, number> = {};
      genres.forEach(genre => {
        loadMoreInit[genre] = 1; // Start with first page
      });
      setLoadMoreByGenre(loadMoreInit);

      // Load books for each genre
      await loadBooksForGenres(genres);

      // Load user's existing ratings
      await loadUserRatings();

      // Load current year's reading goal
      await loadCurrentGoal();

    } catch (error) {
      console.error('Error loading user preferences:', error);
      setError('Failed to load your preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentGoal = async () => {
    const goal = await getGoalForYear(currentYear);
    if (goal) {
      setCurrentGoal(goal);
      setTargetBooks(goal.target_books.toString());
      setTargetPages(goal.target_pages?.toString() || '');
    }
  };

  const loadBooksForGenres = async (genres: string[]) => {
    const newBooksByGenre: Record<string, BookData[]> = {};
    
    for (const genre of genres) {
      const booksForGenre = POPULAR_BOOKS[genre] || [];
      newBooksByGenre[genre] = booksForGenre.slice(0, 16); // Show first 16 books
    }
    
    setBooksByGenre(newBooksByGenre);
  };

  const loadUserRatings = async () => {
    if (!user) return;

    try {
      const { data } = await import('@/integrations/supabase/client').then(m => m.supabase
        .from('user_books')
        .select('book_id, rating_star')
        .eq('user_id', user.id)
        .not('rating_star', 'is', null)
      );

      if (data) {
        const ratings: Record<string, number> = {};
        data.forEach(item => {
          if (item.rating_star) {
            ratings[item.book_id] = item.rating_star;
          }
        });
        setBookRatings(ratings);
      }
    } catch (error) {
      console.error('Error loading user ratings:', error);
    }
  };

  const handleGenreToggle = async (genre: string) => {
    const newSelectedGenres = selectedGenres.includes(genre)
      ? selectedGenres.filter(g => g !== genre)
      : [...selectedGenres, genre];

    setSelectedGenres(newSelectedGenres);

    try {
      // Save genres immediately
      if (user) {
        await saveUserGenres(user.id, newSelectedGenres);
        toast({
          title: "Preferences updated",
          description: `${genre} ${newSelectedGenres.includes(genre) ? 'added to' : 'removed from'} your interests.`,
        });
      }

      // Load books for newly selected genre
      if (!selectedGenres.includes(genre)) {
        const booksForGenre = POPULAR_BOOKS[genre] || [];
        setBooksByGenre(prev => ({
          ...prev,
          [genre]: booksForGenre.slice(0, 16)
        }));
        setLoadMoreByGenre(prev => ({
          ...prev,
          [genre]: 1
        }));
      } else {
        // Remove books for deselected genre
        setBooksByGenre(prev => {
          const newBooks = { ...prev };
          delete newBooks[genre];
          return newBooks;
        });
        setLoadMoreByGenre(prev => {
          const newLoadMore = { ...prev };
          delete newLoadMore[genre];
          return newLoadMore;
        });
      }
    } catch (error) {
      console.error('Error saving genres:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save preferences. Please try again.",
      });
      // Revert the change
      setSelectedGenres(selectedGenres);
    }
  };

  const handleLoadMore = (genre: string) => {
    const currentPage = loadMoreByGenre[genre] || 1;
    const nextPage = currentPage + 1;
    const booksForGenre = POPULAR_BOOKS[genre] || [];
    const startIndex = currentPage * 16;
    const endIndex = nextPage * 16;
    const moreBooksToShow = booksForGenre.slice(startIndex, endIndex);

    if (moreBooksToShow.length > 0) {
      setBooksByGenre(prev => ({
        ...prev,
        [genre]: [...(prev[genre] || []), ...moreBooksToShow]
      }));
      setLoadMoreByGenre(prev => ({
        ...prev,
        [genre]: nextPage
      }));
    }
  };

  const handleRatingChange = async (bookId: string, rating: number) => {
    if (!user) return;

    // Update local state immediately (optimistic update)
    setBookRatings(prev => ({
      ...prev,
      [bookId]: rating
    }));

    try {
      // Save to database
      await saveBookRatings(user.id, [{ bookId, rating }]);
      
      // Trigger pairwise comparison immediately
      const { data, error } = await supabase.functions.invoke('on-rating-saved', {
        body: {
          userId: user.id,
          bookId,
          rating
        }
      });

      if (!error && data?.needsComparison && data?.comparison) {
        // Find the book details to pass to PairwiseSheet
        const bookDetails = Object.values(booksByGenre).flat().find(b => b.id === bookId) || 
                           searchResults.find(b => b.id === bookId);
        
        if (bookDetails) {
          setCurrentRatingBook(bookDetails);
          setPairwiseData(data);
          setShowPairwise(true);
        }
      }
    } catch (error) {
      console.error('Error saving rating:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save rating. Please try again.",
      });
      // Revert the rating on error
      setBookRatings(prev => {
        const updated = { ...prev };
        delete updated[bookId];
        return updated;
      });
    }
  };

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      performSearch(searchQuery, searchType);
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchType]);

  const performSearch = async (query: string, type: 'title' | 'author') => {
    if (!query.trim()) return;

    try {
      setSearchLoading(true);
      
      // Mock search implementation - in real app, this would call an API
      const allBooks = Object.values(POPULAR_BOOKS).flat();
      const results = allBooks.filter(book => {
        if (type === 'title') {
          return book.title.toLowerCase().includes(query.toLowerCase());
        } else {
          return book.authors.some(author => 
            author.toLowerCase().includes(query.toLowerCase())
          );
        }
      }).slice(0, 10); // Limit to 10 results

      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleDone = () => {
    // Navigate back to previous page or profile
    const referrer = location.state?.from;
    if (referrer) {
      navigate(referrer);
    } else if (user) {
      // Try to get username for profile navigation
      import('@/integrations/supabase/client').then(m => m.supabase
        .from('profiles')
        .select('username')
        .eq('id', user.id)
        .single()
      ).then(({ data }) => {
        if (data?.username) {
          navigate(`/u/${data.username}`);
        } else {
          navigate('/');
        }
      }).catch(() => {
        navigate('/');
      });
    } else {
      navigate('/');
    }
  };

  const handleGoalSave = async () => {
    const booksTarget = parseInt(targetBooks);
    const pagesTarget = targetPages ? parseInt(targetPages) : undefined;
    
    if (isNaN(booksTarget) || booksTarget < 1) {
      toast({
        variant: "destructive",
        title: "Invalid Goal",
        description: "Please enter a valid number of books (at least 1).",
      });
      return;
    }

    if (targetPages && (isNaN(pagesTarget!) || pagesTarget! < 1)) {
      toast({
        variant: "destructive", 
        title: "Invalid Goal",
        description: "Please enter a valid number of pages or leave it empty.",
      });
      return;
    }

    const goal = await createOrUpdateGoal(currentYear, booksTarget, pagesTarget);
    if (goal) {
      setCurrentGoal(goal);
    }
  };

  // Get all visible books for deduplication
  const getAllVisibleBooks = () => {
    const visibleBooks = new Set<string>();
    Object.values(booksByGenre).forEach(books => {
      books.forEach(book => visibleBooks.add(book.id));
    });
    searchResults.forEach(book => visibleBooks.add(book.id));
    return visibleBooks;
  };

  if (!user) {
    navigate('/auth');
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-lg">Loading your preferences...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <OnboardingErrorCard
              title="Unable to load preferences"
              message={error}
              onRetry={() => {
                setError(null);
                loadUserPreferences();
              }}
              onBack={() => navigate(-1)}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card/80 backdrop-blur-sm border-b border-border">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <h1 className="text-xl font-heading font-semibold">Reading Preferences</h1>
          <div className="w-16" />
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          
          {/* Reading Goals Section */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                {currentYear} Reading Goal
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Set your reading targets for this year to track your progress.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="target-books">Books to read</Label>
                  <Input
                    id="target-books"
                    type="number"
                    min="1"
                    placeholder="12"
                    value={targetBooks}
                    onChange={(e) => setTargetBooks(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="target-pages">Pages to read (optional)</Label>
                  <Input
                    id="target-pages"
                    type="number"
                    min="1"
                    placeholder="5000"
                    value={targetPages}
                    onChange={(e) => setTargetPages(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  onClick={handleGoalSave} 
                  disabled={goalLoading}
                  size="sm"
                >
                  {goalLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {currentGoal ? 'Update Goal' : 'Set Goal'}
                </Button>
                {currentGoal && (
                  <span className="text-sm text-muted-foreground">
                    Current goal: {currentGoal.target_books} books
                    {currentGoal.target_pages ? `, ${currentGoal.target_pages} pages` : ''}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Notification Preferences Section */}
          <NotificationPreferences />

          {/* Followed Tags Section */}
          <FollowedTagsList />

          {/* Weekly Reading Goal (Streak System) */}
          <ReadingGoalSettings />
          
          {/* Genres Section */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Your Reading Interests</CardTitle>
              <p className="text-sm text-muted-foreground">
                Select genres you enjoy reading. We recommend at least 3 for better book suggestions.
                <span className="ml-2 font-medium">
                  {selectedGenres.length} selected
                </span>
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Fiction Section */}
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Fiction
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {FICTION.map((genre) => (
                      <Badge
                        key={genre}
                        variant={selectedGenres.includes(genre) ? "default" : "outline"}
                        className="cursor-pointer px-4 py-2 text-sm hover:scale-105 transition-transform"
                        onClick={() => handleGenreToggle(genre)}
                      >
                        {genre}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Non-Fiction Section */}
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Non-Fiction
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {NONFICTION.map((genre) => (
                      <Badge
                        key={genre}
                        variant={selectedGenres.includes(genre) ? "default" : "outline"}
                        className="cursor-pointer px-4 py-2 text-sm hover:scale-105 transition-transform"
                        onClick={() => handleGenreToggle(genre)}
                      >
                        {genre}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Search Section */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Quick Search & Rate</CardTitle>
              <p className="text-sm text-muted-foreground">
                Search for any book and rate it quickly
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search for books..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant={searchType === 'title' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSearchType('title')}
                  >
                    Title
                  </Button>
                  <Button
                    variant={searchType === 'author' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSearchType('author')}
                  >
                    Author
                  </Button>
                </div>
              </div>

              {searchLoading && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  <span className="text-sm text-muted-foreground">Searching...</span>
                </div>
              )}

              {searchResults.length > 0 && (
                  <div className="space-y-2 border rounded-lg p-4">
                    <h4 className="font-medium text-sm">Search Results</h4>
                    <div className="grid gap-4">
                      {searchResults.map((book) => (
                        <div key={book.id} className="flex items-center gap-4 p-2 border rounded">
                          <div className="w-12 h-16 bg-muted rounded flex-shrink-0">
                            <CoverImage
                              bookId={book.id}
                              isbn13={book.isbn13}
                              title={book.title}
                              author={book.authors?.[0]}
                              size="xs"
                              className="w-12 h-16"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h5 className="font-medium text-sm truncate">{book.title}</h5>
                            <p className="text-xs text-muted-foreground truncate">
                              by {book.authors.join(', ')}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <StarRating
                              value={bookRatings[book.id] || 0}
                              onChange={(rating) => handleRatingChange(book.id, rating)}
                              size="sm"
                              showLabel={false}
                            />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Books by Genre */}
          {selectedGenres.map((genre) => {
            const books = booksByGenre[genre] || [];
            const allGenreBooks = POPULAR_BOOKS[genre] || [];
            const hasMore = books.length < allGenreBooks.length;

            if (books.length === 0) return null;

            return (
              <Card key={genre} className="card-elevated">
                <CardHeader>
                  <CardTitle className="capitalize">{genre} Books</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Rate books you've read or want to read
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {books.map((book) => (
                      <div key={book.id} className="space-y-2">
                        <div className="aspect-[3/4] bg-muted rounded-lg overflow-hidden">
                          <CoverImage
                            bookId={book.id}
                            isbn13={book.isbn13}
                            title={book.title}
                            author={book.authors?.[0]}
                            coverUrl={book.cover_url}
                            size="sm"
                            className="w-full h-full"
                          />
                        </div>
                        <div className="space-y-1">
                          <h4 className="text-sm font-medium leading-tight line-clamp-2">
                            {book.title}
                          </h4>
                          <p className="text-xs text-muted-foreground line-clamp-1">
                            {book.authors.join(', ')}
                          </p>
                          <StarRating
                            value={bookRatings[book.id] || 0}
                            onChange={(rating) => handleRatingChange(book.id, rating)}
                            size="sm"
                            showLabel={false}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {hasMore && (
                    <div className="mt-4 text-center">
                      <Button
                        variant="outline"
                        onClick={() => handleLoadMore(genre)}
                        className="flex items-center gap-2"
                      >
                        Load More
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {selectedGenres.length === 0 && (
            <Card className="card-elevated">
              <CardContent className="p-8 text-center">
                <Star className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Select Your Interests</h3>
                <p className="text-muted-foreground">
                  Choose some genres above to see personalized book recommendations.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Debug Info */}
          {debugMode && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Debug Info:</strong>
                <br />
                Selected genres: {selectedGenres.length}
                <br />
                Total ratings: {Object.keys(bookRatings).length}
                <br />
                Search query: "{searchQuery}" ({searchType})
                <br />
                Search results: {searchResults.length}
              </AlertDescription>
            </Alert>
          )}

          {/* Done Button */}
          <div className="sticky bottom-0 bg-background/80 backdrop-blur-sm border-t border-border py-4">
            <div className="flex justify-center">
              <Button
                onClick={handleDone}
                size="lg"
                className="px-8"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Done'
                )}
              </Button>
            </div>
          </div>
        </div>
      </main>
      
      {/* Pairwise comparison sheet */}
      {showPairwise && pairwiseData && currentRatingBook && (
        <PairwiseSheet
          isOpen={showPairwise}
          onClose={() => {
            setShowPairwise(false);
            setPairwiseData(null);
            setCurrentRatingBook(null);
          }}
          currentBook={{
            id: currentRatingBook.id,
            title: currentRatingBook.title,
            authors: currentRatingBook.authors,
            cover_url: currentRatingBook.cover_url
          }}
          tier={pairwiseData.tier}
          candidates={[]}
          onComplete={() => {
            setShowPairwise(false);
            setPairwiseData(null);
            setCurrentRatingBook(null);
          }}
          onSkip={() => {
            setShowPairwise(false);
            setPairwiseData(null);
            setCurrentRatingBook(null);
          }}
          placementState={pairwiseData.placementState}
          comparison={pairwiseData.comparison}
        />
      )}
    </div>
  );
}